import { apiClient as api } from './api';

export interface ReportType {
  id: string;
  name: string;
  description: string;
  category: string;
  exportFormats: string[];
}

export interface ProfitLossReport {
  period: {
    startDate: string;
    endDate: string;
  };
  revenue: {
    serviceRevenue: number;
    productRevenue: number;
    totalRevenue: number;
  };
  expenses: {
    operationalExpenses: number;
    staffExpenses: number;
    inventoryExpenses: number;
    marketingExpenses: number;
    otherExpenses: number;
    totalExpenses: number;
  };
  grossProfit: number;
  netProfit: number;
  profitMargin: number;
  revenueBreakdown: Array<{
    category: string;
    amount: number;
    percentage: number;
  }>;
  expenseBreakdown: Array<{
    category: string;
    amount: number;
    percentage: number;
  }>;
}

export interface GSTReport {
  period: {
    startDate: string;
    endDate: string;
  };
  sales: {
    totalSales: number;
    taxableAmount: number;
    gstCollected: number;
    gstRate: number;
  };
  purchases: {
    totalPurchases: number;
    taxableAmount: number;
    gstPaid: number;
  };
  netGST: number;
  transactions: Array<{
    date: string;
    type: 'SALE' | 'PURCHASE';
    amount: number;
    gstAmount: number;
    gstRate: number;
    description: string;
    invoiceNumber?: string;
  }>;
}

export interface CashFlowReport {
  period: {
    startDate: string;
    endDate: string;
  };
  openingBalance: number;
  closingBalance: number;
  operatingActivities: {
    cashFromOperations: number;
    cashFromCustomers: number;
    cashToSuppliers: number;
    cashToEmployees: number;
    netOperatingCash: number;
  };
  investingActivities: {
    equipmentPurchases: number;
    assetSales: number;
    netInvestingCash: number;
  };
  financingActivities: {
    ownerInvestments: number;
    loanProceeds: number;
    loanRepayments: number;
    netFinancingCash: number;
  };
  netCashFlow: number;
}

export interface StaffPerformanceReport {
  period: {
    startDate: string;
    endDate: string;
  };
  staffPerformance: Array<{
    staffId: string;
    staffName: string;
    totalAppointments: number;
    totalRevenue: number;
    averageServiceValue: number;
    customerRating: number;
    commissionEarned: number;
    utilizationRate: number;
    services: Array<{
      serviceName: string;
      count: number;
      revenue: number;
    }>;
  }>;
  topPerformers: Array<{
    staffId: string;
    staffName: string;
    metric: string;
    value: number;
  }>;
}

export interface ExportReportData {
  reportType: 'profit-loss' | 'gst' | 'cash-flow' | 'staff-performance';
  format?: 'csv' | 'pdf';
  startDate: string;
  endDate: string;
}

class ReportService {
  /**
   * Get available report types
   */
  async getReportTypes(): Promise<ReportType[]> {
    const response = await api.get('/reports/types');
    return response.data;
  }

  /**
   * Generate Profit & Loss Report
   */
  async generateProfitLossReport(startDate: string, endDate: string): Promise<ProfitLossReport> {
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);

    const response = await api.get(`/reports/profit-loss?${params.toString()}`);
    return response.data;
  }

  /**
   * Generate GST Report
   */
  async generateGSTReport(startDate: string, endDate: string): Promise<GSTReport> {
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);

    const response = await api.get(`/reports/gst?${params.toString()}`);
    return response.data;
  }

  /**
   * Generate Cash Flow Report
   */
  async generateCashFlowReport(startDate: string, endDate: string): Promise<CashFlowReport> {
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);

    const response = await api.get(`/reports/cash-flow?${params.toString()}`);
    return response.data;
  }

  /**
   * Generate Staff Performance Report
   */
  async generateStaffPerformanceReport(
    startDate: string,
    endDate: string
  ): Promise<StaffPerformanceReport> {
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);

    const response = await api.get(`/reports/staff-performance?${params.toString()}`);
    return response.data;
  }

  /**
   * Export report in specified format
   */
  async exportReport(data: ExportReportData): Promise<Blob> {
    const response = await fetch('/api/v1/reports/export', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to export report');
    }

    return response.blob();
  }

  /**
   * Download exported report
   */
  async downloadReport(data: ExportReportData, filename?: string): Promise<void> {
    const blob = await this.exportReport(data);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const extension = data.format === 'pdf' ? 'pdf' : 'csv';
    const defaultFilename = `${data.reportType}-report-${data.startDate}-to-${data.endDate}.${extension}`;

    link.download = filename || defaultFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

export const reportService = new ReportService();
